<template>
    <div class="survey-app">

        <div class="survey-app-content">

            <Card v-if="loading" class="survey-app-card" :loading="loading">

                <template #card-title>
                    {{ hasNoSelection ? 'Surveys' : 'Survey' }}
                </template>

                <template #card-content>
                    <div class="survey-app-inner">
                        loading...
                    </div>
                </template>

            </Card>

            <!-- Overview for All Surveys -->
            <Card v-else-if="hasNoSelection" class="survey-app-card">

                <template #card-title>
                    Surveys
                </template>

                <template #card-content>

                    <div class="survey-app-inner">

                        <div v-for="template in templates" class="survey-app-item">
                            <div class="survey-app-grow">
                                {{ template.title }}
                            </div> 
                            <div class="survey-app-shrink">
                                <v-btn 
                                    color="surface" 
                                    elevation="0" 
                                    @click="handleSelect(template.id)">
                                    open survey
                                </v-btn>
                            </div>
                        </div>

                        <div v-if="!hasTemplates" class="survey-app-card-inner">
                            <v-icon class="survey-icon" icon="fa-solid fa-clipboard-list"></v-icon>
                            <span class="survey-span">No surveys active at the moment.</span>
                        </div>

                    </div>

                    <hr v-if="answeredSurveysCount > 0" class="survey-app-hr" />

                    <div v-if="answeredSurveysCount > 0" class="survey-app-inner">
                        <span v-if="answeredSurveysCount === 1">You have answered one survey in the past. Thank you! 🙏</span>
                        <span v-else-if="answeredSurveysCount > 1">You have answered {{ answeredSurveysCount }} surveys in the past. Thank you! 🙏</span>
                    </div>

                </template>

            </Card>

            <!-- Available Survey -->
            <Card v-else-if="hasAvailableSelection" class="survey-app-card">

                <template #card-title>
                    Survey&nbsp;|&nbsp;{{ selectedTemplateTitle }}
                </template>

                <template #card-content>

                    <div v-if="finished" class="survey-app-card-inner">
                        <v-icon class="survey-icon" icon="fa-solid fa-clipboard-list"></v-icon>
                        <span class="survey-span">
                            We have received your response. Thank you for your feedback!
                        </span>
                        <v-btn 
                            class="survey-button" 
                            elevation="0" 
                            @click="handleSelect(null)">
                            Back to All Surveys
                        </v-btn>
                    </div>

                    <div v-else class="survey-app-card-inner">

                        <Alert 
                            class="survey-app-message"
                            :title="selectedTemplateInfoTitle" 
                            :text="selectedTemplateInfoText" />

                        <component 
                            v-for="(item, index) in selectedTemplateItems" 
                            :key="index" 
                            :is="components[item.type]"
                            :index="index"
                            :question="item.question"
                            :options="item.options" 
                            @answer="handleAnswer">
                        </component>

                        <v-btn 
                            class="survey-button" 
                            elevation="0" 
                            @click="handleSend">
                            Send Answer
                        </v-btn>

                    </div>

                </template>

            </Card>

            <!-- Answered Survey -->
            <Card v-else-if="hasAnsweredSelection" class="survey-app-card">

                <template #card-title>
                    {{ selectedTemplateTitle }}
                </template>

                <template #card-content>

                    <div class="survey-app-card-inner">
                        <v-icon class="survey-icon" icon="fa-solid fa-clipboard-list"></v-icon>
                        <span class="survey-span">
                            You have already answered the survey. Thank you for your feedback! 🙏
                        </span>
                        <v-btn 
                            class="survey-button" 
                            elevation="0" 
                            @click="handleSelect(null)">
                            Back to All Surveys
                        </v-btn>
                    </div>

                </template>

            </Card>

            <!-- No Survey Available -->
            <Card v-else class="survey-app-card">

                <template #card-title>
                    Survey
                </template>

                <template #card-content>
                    <div class="survey-app-card-inner">
                        <v-icon class="survey-icon" icon="fa-solid fa-clipboard-list"></v-icon>
                        <span class="survey-span">
                            Please check the link, as it appears there is no survey available.
                        </span>
                        <v-btn 
                            class="survey-button" 
                            elevation="0" 
                            @click="handleSelect(null)">
                            Back to All Surveys
                        </v-btn>
                    </div>
                </template>

            </Card>

        </div>

    </div>
</template>

<script>
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import Card from '@/components/common/cards/Card.vue'
import Alert from '@/components/common/alert/Alert.vue'
import QuestionText from '@/components/survey/question/QuestionText.vue'
import QuestionChoice from '@/components/survey/question/QuestionChoice.vue'
import QuestionScale from '@/components/survey/question/QuestionScale.vue'

export default {
    name: 'SurveyApp',
    components: {
        Card,
        Alert,
        QuestionText,
        QuestionChoice,
        QuestionScale
    },
    data() {
        return {
            id: null,
            loading: false,
            finished: false,
            surveys: null,
            templates: [],
            answers: [],
        }
    },
    computed: {
        hasNoSelection() {
            return this.id === null
        },
        hasAvailableSelection() {
            return this.id !== null && this.selectedTemplateAvailable
        },
        hasAnsweredSelection() {
            return this.id !== null && this.answeredSurveys?.includes(this.id)
        },
        hasTemplates() {
            return this.templates?.length > 0
        },

        availableSurveys() {
            return this.surveys?.available || []
        },
        answeredSurveys() {
            return this.surveys?.answered || []
        },
        answeredSurveysCount() {
            return this.answeredSurveys?.length || 0
        },

        selectedTemplate() {
            return this.templates?.find(template => template.id === this.id) || null
        },
        selectedTemplateAvailable() {
            return this.selectedTemplate?.available || false
        },
        selectedTemplateTitle() {
            return this.selectedTemplate?.title || null
        },
        selectedTemplateInfoTitle() {
            return this.selectedTemplate?.info?.title || ''
        },
        selectedTemplateInfoText() {
            return this.selectedTemplate?.info?.text || ''
        },
        selectedTemplateItems() {
            return this.selectedTemplate?.items || []
        },
        
        components() {
            return {
                text: QuestionText,
                choice: QuestionChoice,
                scale: QuestionScale
            }
        },

        /*
        hasAnswers() {
            for (const item of this.survey?.items) {
                if (item?.answer !== undefined) return true
            }
            return false
        }
        */
    },
    methods: {
        ...mapActions('api', [
            'analyticsSurveysGet',
            'analyticsSurveysSurveyGet',
            'analyticsSurveySurveyPut'
        ]),
        async init() {
            // set local state
            this.loading = true
            this.finished = false
            this.surveys = null
            this.templates = []
            this.answers = []

            try 
            {
                // retrieve all surveys
                const responseSurveys = await this.analyticsSurveysGet()
                this.surveys = {
                    available: responseSurveys.surveys?.available || [],
                    answered: responseSurveys.surveys?.answered || [],
                }

                // iterate through the available surveys
                for (const id of this.surveys.available) 
                {
                    // retrieve current survey
                    const responseSurvey = await this.analyticsSurveysSurveyGet({ id })
                    const template = responseSurvey.survey
                    this.templates.push(template)
                }
            }
            finally 
            {
                // set loading state
                this.loading = false
            }
        },
        handleSelect(id) {
            if (id === null) this.init()

            // set survey id
            this.id = id
        },
        handleAnswer(index, answer) {
            this.answers[index] = answer
        },
        async handleSend() {
            // set loading state
            this.loading = true

            try 
            {
                // ensure that answers are as long as the questions
                while (this.answers.length < this.selectedTemplateItems.length) {
                    this.answers.push(null)
                }

                // send answers to server
                await this.analyticsSurveySurveyPut({
                    id: this.id,
                    answers: this.answers
                })

                // mark as finished
                this.finished = true
            }
            catch (error) 
            {
                console.log(error)
                this.show({
                    message: 'Unable to send response. Please try again.',
                    level: LEVELS.ERROR
                })
            }
            finally 
            {
                // set loading state
                this.loading = false
            }
        }
    },     
    mounted() {
        if (this.$route.query.id) {
            this.id = this.$route.query.id
        }
        this.init()
    }
}
</script>

<style>
@media (max-width: 800px) {
    .v-selection-control-group--inline {
        flex-direction: column !important;
    }
}
</style>

<style scoped>
.survey-app {
    width: 100%;
    max-height: 100vh;
    display: block;
    overflow-y: auto;
    color: rgb(var(--v-theme-primary));
}

.survey-app-content {
    width: 100%;
    padding: 25px;
    padding-top: 50px;
    padding-bottom: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.survey-app-card {
    width: 100%;
    max-width: 1200px;
}

.survey-app-card-inner {
    padding: 40px;
    padding-top: 20px;
    padding-bottom: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 40px;
}

.survey-app-inner {
    font-size: 1.1rem;
    padding-left: 25px;
    padding-right: 25px;
}

.survey-app-hr {
    margin-top: 20px;
    margin-bottom: 20px;
    color: rgb(var(--v-theme-lightbase)) !important;
    border-color: rgb(var(--v-theme-lightbase)) !important;
    opacity: 0.2;
}

.survey-app-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 8px;
    padding: 20px;
    text-align: left;
    background-color: rgb(var(--v-theme-color1));
}

.survey-app-grow {
    flex-grow: 1;
}

.survey-app-shrink {
    flex-shrink: 1;
}

.survey-span {
    font-size: 1.2rem;
}

.survey-icon {
    font-size: 5rem;
}

.survey-button:hover {
    color: rgb(var(--v-theme-white));
    background-color: rgb(var(--v-theme-color1));
}

@media (max-width: 1000px) {
    .survey-app-card-inner {
        padding: 20px;
    }
}

@media (max-width: 800px) {
    .survey-app-content {
        padding: 15px;
        padding-bottom: 75px;
    }

    .survey-app-card-inner {
        padding: 15px;
    }
}
</style>